import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Ikon,
  Iskelet,
  MetinAlani,
  Rozet,
  SatirDugmesi,
  SatirMetni,
  Secim,
  anMetni,
  Kart,
  SekmeHapi,
  Sekmeler,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  agacGetir,
  agactakiKonular,
  alanEkle,
  alanlariGetir,
  dersTopluEkle,
  konuArsivle,
  konuSirasiDegistir,
  konuTopluEkle,
  onKosulEkle,
  onKosulKaldir,
  onKosullariGetir,
  surumEkle,
  surumYayinla,
  surumeKonuAta,
  surumleriGetir,
  topluPartiGeriAl,
  topluPartileriGetir,
  uniteEkle,
  yilEkle,
  yillariGetir,
  type AgacDugumu,
  type PartiSatiri,
  type PartiTuru,
  type TopluSonuc,
  type YilSatiri,
} from "./api";
import { SayfaBasligiEki } from "./Kabuk";
import { konuKimlikleri, kumeyiDegistir, secimDurumu, type SecimDurumu } from "./konu-secimi";

/**
 * MÜFREDAT YÖNETİMİ — F-ACA
 *
 * ================================================================
 * ZİNCİRİN ÜÇÜNCÜ HALKASI
 * ================================================================
 * `akademik/*` altındaki on yazma ucu sunucuda vardı ama panel
 * yalnızca OKUYORDU (`/alanlar` ve `/agac`). Müfredat kurulamadığı
 * için haftalık program da kurulamıyordu: program görevleri konuya
 * bağlanıyor ve ortada konu yoktu.
 *
 * ================================================================
 * ÖNİZLEME AYRI BİR UÇ DEĞİL
 * ================================================================
 * Toplu giriş `onIzleme` bayrağıyla çalışıyor: aynı uç, aynı
 * ayrıştırıcı, yalnızca yazma yok. İki ayrı uç olsaydı önizlemenin
 * gösterdiği ile uygulamanın yaptığı bir gün ayrışır ve kullanıcı
 * gördüğünden başkasını kaydederdi.
 *
 * ================================================================
 * ELENEN SATIR SESSİZCE KAYBOLMUYOR
 * ================================================================
 * `toplu-giris.ts` gerekçesi: 200 satırlık bir müfredat yapıştırılıp
 * 180'i kaydedilirse kullanıcı eksik 20 satırı fark etmez ve o
 * konular yıl boyunca hiçbir programa girmez. Ekran elenen her satırı
 * numarası ve sebebiyle gösteriyor.
 */

/**
 * SEÇİLENDEN AZI EKLENDİYSE SEBEBİNİ SÖYLE.
 *
 * Sunucu zaten sürümde olan konuyu atlıyor ve yalnızca `eklenen`
 * sayısını dönüyor. 02.09.2026'da canlı denemede görüldü: 2 konu
 * seçildi, ekran "1 konu eklendi" yazdı ve aradaki farkın sebebi
 * hiçbir yerde yazmıyordu — kullanıcı bir konunun neden düştüğünü
 * bilemiyordu.
 *
 * `TopluGiris` bölümündeki kuralın aynısı: elenen satır sessizce
 * kaybolmaz. Sunucuyu değiştirmeye gerek yok — istemci kaç tane
 * gönderdiğini zaten biliyor.
 */
export function atamaOzeti(istenen: number, eklenen: number): string {
  const temel = `${eklenen} konu eklendi.`;
  if (istenen <= eklenen) return temel;
  return `${temel} ${istenen - eklenen} tanesi bu sürümde zaten vardı.`;
}

function dersOzeti(ders: AgacDugumu): string {
  const cocuklar = ders.cocuklar ?? [];
  const uniteSayisi = cocuklar.filter((c) => c.tur === "unite").length;
  const konuSayisi = agactakiKonular(cocuklar).length;
  if (uniteSayisi === 0) return `${konuSayisi} konu`;
  return `${uniteSayisi} ünite · ${konuSayisi} konu`;
}

const SURUM_DURUMU: Record<string, { etiket: string; ton: "notr" | "onay" | "uyari" }> = {
  DRAFT: { etiket: "Taslak", ton: "uyari" },
  PUBLISHED: { etiket: "Yayında", ton: "onay" },
  ARCHIVED: { etiket: "Arşiv", ton: "notr" },
};

export function Mufredat({ duzenleyebilir = false }: { duzenleyebilir?: boolean }) {
  const [gorunum, setGorunum] = useState<"yapi" | "surumler" | "partiler">("yapi");

  return (
    <div className="flex flex-col gap-6">
      {/* UNİKOÇ "Ders İşlemleri" bandı (tarama-1 §3.14): açıklama bantta, sekmeler hap. */}
      <SayfaBasligiEki aciklama="Ders, konu ve kaynakları alan bazında yönetin; sürümleri ve toplu girişleri izleyin." />
      {/*
        ================================================================
        AKTİF GÖRÜNÜM SADECE RENKLE SÖYLENİYORDU
        ================================================================
        Şerit `gorunum="birincil"`/`"sessiz"` ile çiziliyordu: hangi
        görünümün açık olduğu YALNIZCA rengin farkıydı. Ekran okuyucu
        üç düğmeyi de birbirinin aynı duyuruyordu — kullanıcı hangisinde
        olduğunu bilemiyor, "Sürümler"e basıp bir şey değişti mi diye
        anlayamıyordu.

        `Sekmeler` bu işin deponun kendi cevabı: `aria-pressed` taşıyan
        basılı-kalan düğmeler. `role="tab"` DEĞİL — ok tuşu gezinmesi ve
        `tabpanel` bağı olmadan ARIA sekme deseni yarım kalır.
      */}
      <Sekmeler
        gorunum="hap"
        etiket="Müfredat görünümü"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={[
          { deger: "yapi", etiket: "Ders ve konular" },
          { deger: "surumler", etiket: "Sürümler" },
          { deger: "partiler", etiket: "Toplu girişler" },
        ]}
      />

      {gorunum === "yapi" && <Yapi duzenleyebilir={duzenleyebilir} />}
      {gorunum === "surumler" && <Surumler duzenleyebilir={duzenleyebilir} />}
      {gorunum === "partiler" && <TopluPartiler duzenleyebilir={duzenleyebilir} />}
    </div>
  );
}

/**
 * SON TOPLU GİRİŞLER — F-ACA-03b
 *
 * ================================================================
 * GERİ ALMA, SİLME DEĞİL ARŞİVLEME
 * ================================================================
 * Bu içe aktarmanın oluşturduğu kayıtlar arşivlenir; geçmiş
 * programlar bozulmaz (CLAUDE.md §7).
 *
 * ================================================================
 * KULLANILMIŞ KAYIT ENGELLEMİYOR, SAYILIYOR
 * ================================================================
 * Sunucunun kararı ve gerekçesi orada yazılı: engelleseydi tek bir
 * kullanım bütün partiyi kilitlerdi, sessizce arşivleseydi kullanıcı
 * ne kırdığını bilmezdi. Ekran o sayıyı GÖSTERİYOR.
 *
 * Liste yalnızca GERİ ALABİLECEĞİNİZ türleri taşıyor — süzgeç
 * sunucuda. İzni olmayana "şunları geri alabilirsin" gösterip her
 * denemede 403 vermek çıkmaz sokak olurdu.
 */
function TopluPartiler({ duzenleyebilir }: { duzenleyebilir: boolean }) {
  const partiler = useQuery({
    queryKey: ["toplu-partiler"],
    queryFn: () => topluPartileriGetir(),
    retry: false,
  });

  if (partiler.isPending) return <Iskelet satir={3} />;

  if (partiler.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Toplu girişler alınamadı"
        aciklama={partiler.error instanceof ApiHatasi ? partiler.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void partiler.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (partiler.data.length === 0) {
    return (
      <BosDurum
        ikon="mufredat"
        baslik="Geri alınabilecek toplu giriş yok"
        aciklama="Toplu ders, konu veya kaynak girişi yaptığınızda buraya düşer ve gerekçeyle geri alınabilir."
      />
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {partiler.data.map((p) => (
        <li key={p.id}>
          <PartiSatiriGorunum parti={p} duzenleyebilir={duzenleyebilir} />
        </li>
      ))}
    </ul>
  );
}

const PARTI_TURU: Record<PartiTuru, string> = {
  Lesson: "Ders",
  Topic: "Konu",
  ResourceBook: "Kaynak kitap",
  StudentResourceBook: "Öğrenci kaynak listesi",
};

function PartiSatiriGorunum({
  parti,
  duzenleyebilir,
}: {
  parti: PartiSatiri;
  duzenleyebilir: boolean;
}) {
  const [acik, setAcik] = useState(false);

  return (
    <div className="rounded-kart border-notr-200 shadow-kart flex flex-col gap-2 border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-notr-900 text-sm font-medium">
            {PARTI_TURU[parti.tur] ?? parti.tur} · {parti.kayitSayisi} kayıt
          </p>
          <p className="text-notr-600 text-xs">
            {anMetni(parti.tarih)}
            {parti.yapan && ` · ${parti.yapan}`}
          </p>
        </div>
        {parti.geriAlindiMi ? (
          <Rozet ton="notr">Geri alındı</Rozet>
        ) : (
          duzenleyebilir && (
            <Buton gorunum="ikincil" onClick={() => setAcik((a) => !a)}>
              {acik ? "Kapat" : "Geri al"}
            </Buton>
          )
        )}
      </div>

      {parti.geriAlindiMi && (
        <p className="text-notr-600 text-xs">
          {parti.geriAlmaTarihi && anMetni(parti.geriAlmaTarihi)}
          {parti.geriAlan && ` · ${parti.geriAlan}`}
          {parti.geriAlmaGerekcesi && ` — “${parti.geriAlmaGerekcesi}”`}
        </p>
      )}

      {acik && <GeriAlmaFormu partiId={parti.id} onKapat={() => setAcik(false)} />}
    </div>
  );
}

/** Toplu girişin hemen ardındaki geri alma — açılıp kapanan kısa hâl. */
function SonGirisiGeriAl({ partiId, tazele }: { partiId: string; tazele: unknown[] }) {
  const qc = useQueryClient();
  const [acik, setAcik] = useState(false);

  if (!acik) {
    return (
      <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
        Bu girişi geri al
      </Buton>
    );
  }

  return (
    <GeriAlmaFormu
      partiId={partiId}
      onKapat={() => setAcik(false)}
      sonra={async () => {
        await qc.invalidateQueries({ queryKey: tazele });
      }}
    />
  );
}

function GeriAlmaFormu({
  partiId,
  onKapat,
  sonra,
}: {
  partiId: string;
  onKapat: () => void;
  sonra?: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const [gerekce, setGerekce] = useState("");

  const geriAl = useMutation({
    mutationFn: () => topluPartiGeriAl(partiId, gerekce.trim()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["toplu-partiler"] });
      await qc.invalidateQueries({ queryKey: ["agac"] });
      await sonra?.();
    },
  });

  if (geriAl.isSuccess) {
    const s = geriAl.data;
    return (
      <div className="rounded-alan border-onay-200 bg-onay-50 text-onay-700 border p-3 text-sm">
        {s.arsivlenen} kayıt arşivlendi.
        {/*
          KULLANILMIŞ KAYIT SAYISI SÖYLENİYOR. Sunucu engellemiyor —
          engelleseydi tek bir kullanım bütün partiyi kilitlerdi. Ama
          sessiz kalsaydı kullanıcı ne kırdığını bilmezdi.
        */}
        {s.kullanilan > 0 && (
          <>
            {" "}
            <strong>{s.kullanilan} tanesi bu arada kullanılmıştı</strong> — o kayıtlara bağlı geçmiş
            program ve sonuçlar yerinde duruyor, yalnızca yeni kullanıma kapandı.
          </>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-kart shadow-kart border-uyari-200 bg-uyari-50 flex flex-col gap-2 border p-3">
      <p className="text-uyari-800 text-xs">
        Bu içe aktarmanın oluşturduğu kayıtlar <strong>arşivlenecek</strong>. Silinmiyor: geçmiş
        programlar ve sonuçlar yerinde kalıyor. <strong>Bir kez</strong> geri alınabilir.
      </p>
      <Alan
        etiket="Gerekçe"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        yardim="Denetim kaydına yazılıyor."
      />
      {geriAl.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {geriAl.error instanceof ApiHatasi ? geriAl.error.message : "Geri alınamadı."}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Buton
          yukleniyor={geriAl.isPending}
          disabled={gerekce.trim().length < 3}
          onClick={() => geriAl.mutate()}
        >
          Geri al
        </Buton>
        <Buton gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------

function Yapi({ duzenleyebilir }: { duzenleyebilir: boolean }) {
  const [alanId, setAlanId] = useState<string | null>(null);
  const [yeniAlan, setYeniAlan] = useState(false);

  const alanlar = useQuery({ queryKey: ["alanlar"], queryFn: alanlariGetir, retry: false });

  if (alanlar.isPending) return <Iskelet satir={4} />;

  if (alanlar.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Alanlar alınamadı"
        aciklama={alanlar.error instanceof ApiHatasi ? alanlar.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void alanlar.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (yeniAlan) return <YeniAlan onKapat={() => setYeniAlan(false)} />;

  const secili = alanlar.data.find((a) => a.id === alanId) ?? null;

  if (alanlar.data.length === 0) {
    return (
      <BosDurum
        ikon="mufredat"
        baslik="Henüz alan tanımlanmamış"
        aciklama="Müfredat alanla başlar (YKS Sayısal, LGS gibi). Alan açılmadan ders ve konu girilemez."
        eylem={
          duzenleyebilir ? <Buton onClick={() => setYeniAlan(true)}>Yeni alan</Buton> : undefined
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ul className="flex flex-wrap gap-1">
          {alanlar.data.map((a) => (
            <li key={a.id}>
              <SekmeHapi
                secili={alanId === a.id}
                onClick={() => setAlanId(alanId === a.id ? null : a.id)}
              >
                {a.name}
              </SekmeHapi>
            </li>
          ))}
        </ul>
        {duzenleyebilir && (
          <Buton gorunum="ikincil" onClick={() => setYeniAlan(true)}>
            Yeni alan
          </Buton>
        )}
      </div>

      {secili === null ? (
        <BosDurum
          ikon="mufredat"
          baslik="Alan seçin"
          aciklama="Dersleri ve konuları görmek için yukarıdan bir alan seçin."
        />
      ) : (
        <AlanAgaci alanId={secili.id} alanAdi={secili.name} duzenleyebilir={duzenleyebilir} />
      )}
    </div>
  );
}

function YeniAlan({ onKapat }: { onKapat: () => void }) {
  const qc = useQueryClient();
  const [kod, setKod] = useState("");
  const [ad, setAd] = useState("");

  const ekle = useMutation({
    mutationFn: () => alanEkle({ code: kod.trim(), name: ad.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["alanlar"] });
      onKapat();
    },
  });

  return (
    <Kart
      gorunum="unk"
      baslik="Yeni alan"
      aciklama="Alan, müfredatın en üst basamağı: YKS Sayısal, LGS gibi."
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Kod"
            value={kod}
            onChange={(e) => setKod(e.target.value)}
            yardim="Kısa ve tekil: YKS_SAY, LGS."
          />
          <Alan etiket="Ad" value={ad} onChange={(e) => setAd(e.target.value)} />
        </div>

        {ekle.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {ekle.error instanceof ApiHatasi ? ekle.error.message : "Alan eklenemedi."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={ekle.isPending}
            disabled={kod.trim().length === 0 || ad.trim().length === 0}
            onClick={() => ekle.mutate()}
          >
            Alanı ekle
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

function AlanAgaci({
  alanId,
  alanAdi,
  duzenleyebilir,
}: {
  alanId: string;
  alanAdi: string;
  duzenleyebilir: boolean;
}) {
  const [acikDers, setAcikDers] = useState<string | null>(null);
  const [dersEkleAcik, setDersEkleAcik] = useState(false);

  const agac = useQuery({
    queryKey: ["agac", alanId],
    queryFn: () => agacGetir(alanId),
    retry: false,
  });

  if (agac.isPending) return <Iskelet satir={4} />;

  if (agac.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Ders ağacı alınamadı"
        aciklama={agac.error instanceof ApiHatasi ? agac.error.message : undefined}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-notr-600 text-sm">
          {alanAdi} · {agac.data.length} ders
        </p>
        {duzenleyebilir && (
          <Buton gorunum="ikincil" onClick={() => setDersEkleAcik((a) => !a)}>
            {dersEkleAcik ? "Kapat" : "Toplu ders ekle"}
          </Buton>
        )}
      </div>

      {dersEkleAcik && (
        <TopluGiris
          baslik="Toplu ders ekle"
          aciklama="Her satır bir ders. İkinci sütun (sekme ya da noktalı virgül) kısa koddur."
          ornek={"Matematik\tMAT\nFizik\tFZK\nTürk Dili ve Edebiyatı\tEDB"}
          calistir={(metin, onIzleme) => dersTopluEkle(alanId, metin, onIzleme)}
          tazele={["agac", alanId]}
        />
      )}

      {agac.data.length === 0 ? (
        <BosDurum
          ikon="mufredat"
          baslik="Bu alanda ders yok"
          aciklama="Dersleri tek tek yazmak yerine listeyi olduğu gibi yapıştırabilirsiniz."
          eylem={
            duzenleyebilir ? (
              <Buton onClick={() => setDersEkleAcik(true)}>Toplu ders ekle</Buton>
            ) : undefined
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {agac.data.map((ders) => (
            <li key={ders.id} className="flex flex-col gap-2">
              <SatirDugmesi onClick={() => setAcikDers(acikDers === ders.id ? null : ders.id)}>
                <Ikon ad="mufredat" className="text-notr-400" />
                {/* Ağaç artık `tur` taşıyor — "konu / ünite" diye kaçamak
                    yazmak yerine ikisi ayrı sayılıyor. */}
                <SatirMetni baslik={ders.ad} altBaslik={dersOzeti(ders)} />
                <Ikon ad={acikDers === ders.id ? "tamam" : "bekliyor"} className="text-notr-300" />
              </SatirDugmesi>

              {acikDers === ders.id && (
                <DersAyrintisi
                  ders={ders}
                  duzenleyebilir={duzenleyebilir}
                  alanId={alanId}
                  butunKonular={agactakiKonular(agac.data)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DersAyrintisi({
  ders,
  duzenleyebilir,
  alanId,
  butunKonular,
}: {
  ders: AgacDugumu;
  duzenleyebilir: boolean;
  alanId: string;
  /** Alandaki HER konu — ön koşul seçimi ders sınırını aşabilir. */
  butunKonular: AgacDugumu[];
}) {
  const [konuEkleAcik, setKonuEkleAcik] = useState(false);
  const [uniteEkleAcik, setUniteEkleAcik] = useState(false);
  const cocuklar = ders.cocuklar ?? [];

  return (
    <div className="rounded-kart border-notr-200 shadow-kart ml-4 flex flex-col gap-3 border p-4">
      {duzenleyebilir && (
        <div className="flex flex-wrap gap-2">
          <Buton gorunum="ikincil" onClick={() => setKonuEkleAcik((a) => !a)}>
            {konuEkleAcik ? "Kapat" : "Toplu konu ekle"}
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setUniteEkleAcik((a) => !a)}>
            {uniteEkleAcik ? "Kapat" : "Ünite ekle"}
          </Buton>
        </div>
      )}

      {uniteEkleAcik && (
        <YeniUnite dersId={ders.id} alanId={alanId} onKapat={() => setUniteEkleAcik(false)} />
      )}

      {konuEkleAcik && (
        <TopluGiris
          baslik={`${ders.ad} — toplu konu ekle`}
          aciklama="Her satır bir konu. İkinci sütun ünite adıdır; eşleşmezse konu ÜNİTESİZ eklenir ve satır elenmez."
          ornek={"Fonksiyonlar\tCebir\nTürev\tAnaliz\nİntegral\tAnaliz"}
          calistir={(metin, onIzleme) => konuTopluEkle(ders.id, metin, onIzleme)}
          tazele={["agac", alanId]}
        />
      )}

      {cocuklar.length === 0 ? (
        <p className="text-notr-600 text-sm">Bu derste henüz konu yok.</p>
      ) : (
        <KonuAgaci
          dugumler={cocuklar}
          alanId={alanId}
          duzenleyebilir={duzenleyebilir}
          butunKonular={butunKonular}
        />
      )}
    </div>
  );
}

function YeniUnite({
  dersId,
  alanId,
  onKapat,
}: {
  dersId: string;
  alanId: string;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [ad, setAd] = useState("");

  const ekle = useMutation({
    mutationFn: () => uniteEkle(dersId, ad.trim()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["agac", alanId] });
      onKapat();
    },
  });

  return (
    <Kart
      gorunum="unk"
      baslik="Yeni ünite"
      aciklama="Ünite ZORUNLU değil — ünitesiz konu geçerli bir konudur. Toplu konu girişinde ikinci sütun bu ünitelerle eşleşir."
    >
      <div className="flex flex-col gap-4">
        <Alan
          etiket="Ünite adı"
          value={ad}
          onChange={(e) => setAd(e.target.value)}
          yardim="Örnek: Analiz, Cebir, Geometri."
        />
        {ekle.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {ekle.error instanceof ApiHatasi ? ekle.error.message : "Ünite eklenemedi."}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={ekle.isPending}
            disabled={ad.trim().length === 0}
            onClick={() => ekle.mutate()}
          >
            Üniteyi ekle
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

/** Ünite başlık, konu satır. Ayrım ağacın `tur` alanından geliyor. */
function KonuAgaci({
  dugumler,
  alanId,
  duzenleyebilir,
  butunKonular,
  derinlik = 0,
}: {
  dugumler: AgacDugumu[];
  alanId: string;
  duzenleyebilir: boolean;
  butunKonular: AgacDugumu[];
  derinlik?: number;
}) {
  // Yalnizca KONU dugumleri; uniteler siralanmiyor.
  const konular = dugumler.filter((x) => x.tur === "konu");

  return (
    <ul className="flex flex-col gap-1" style={{ marginInlineStart: derinlik * 12 }}>
      {dugumler.map((d) =>
        d.tur === "unite" ? (
          <li key={d.id}>
            <p className="text-notr-600 mt-2 text-xs font-medium tracking-wide uppercase">{d.ad}</p>
            {(d.cocuklar ?? []).length === 0 ? (
              <p className="text-notr-600 text-sm">Bu ünitede konu yok.</p>
            ) : (
              <KonuAgaci
                dugumler={d.cocuklar ?? []}
                alanId={alanId}
                duzenleyebilir={duzenleyebilir}
                butunKonular={butunKonular}
                derinlik={derinlik + 1}
              />
            )}
          </li>
        ) : (
          <li key={d.id}>
            <KonuSatiri
              konu={d}
              alanId={alanId}
              duzenleyebilir={duzenleyebilir}
              butunKonular={butunKonular}
              /*
                Konum KARDESLER arasinda hesaplaniyor, listedeki ham
                indisle degil: uniteler de ayni dizide duruyor ve
                onlari sayarsak ilk konu "ilk degil" gorunurdu.
              */
              ilkMi={konular[0]?.id === d.id}
              sonMu={konular[konular.length - 1]?.id === d.id}
            />
            {d.cocuklar && d.cocuklar.length > 0 && (
              <KonuAgaci
                dugumler={d.cocuklar}
                alanId={alanId}
                duzenleyebilir={duzenleyebilir}
                butunKonular={butunKonular}
                derinlik={derinlik + 1}
              />
            )}
          </li>
        ),
      )}
    </ul>
  );
}

function KonuSatiri({
  konu,
  alanId,
  duzenleyebilir,
  butunKonular,
  ilkMi,
  sonMu,
}: {
  konu: AgacDugumu;
  alanId: string;
  duzenleyebilir: boolean;
  butunKonular: AgacDugumu[];
  /** Kardesleri arasinda en ustte mi — yukari dugmesi kapali olsun. */
  ilkMi: boolean;
  sonMu: boolean;
}) {
  const qc = useQueryClient();
  const [acik, setAcik] = useState<"kapali" | "onkosul" | "arsiv">("kapali");

  /*
   * ================================================================
   * SIRALAMA SURUKLE-BIRAK DEGIL, DUGME
   * ================================================================
   * Teslim ciziminin kendi cumlesi ve gerekcesi: surukle-birak
   * dokunmatikte ve klavyede zahmetli, 200 konuluk bir agacta
   * neredeyse kullanilamaz. Dugmeler 44px ve klavyeyle erisilebilir.
   *
   * UCTAKI DUGME KAPALI: sunucu "degisti: false" donuyor ve bu hata
   * degil, ama basilabilir gorunen bir dugmenin hicbir sey yapmamasi
   * kullaniciya sistemin bozuk oldugunu dusundururdu.
   */
  const tasi = useMutation({
    mutationFn: (yon: "yukari" | "asagi") => konuSirasiDegistir(konu.id, yon),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["agac", alanId] });
    },
  });

  return (
    <div className="flex flex-col gap-2 py-0.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-notr-700 text-sm">{konu.ad}</span>
        {duzenleyebilir && (
          <div className="flex flex-wrap gap-1">
            <Buton
              gorunum="sessiz"
              disabled={ilkMi || tasi.isPending}
              aria-label={`${konu.ad} konusunu yukarı taşı`}
              onClick={() => tasi.mutate("yukari")}
            >
              ↑
            </Buton>
            <Buton
              gorunum="sessiz"
              disabled={sonMu || tasi.isPending}
              aria-label={`${konu.ad} konusunu aşağı taşı`}
              onClick={() => tasi.mutate("asagi")}
            >
              ↓
            </Buton>
            <Buton
              gorunum="sessiz"
              onClick={() => setAcik((a) => (a === "onkosul" ? "kapali" : "onkosul"))}
            >
              Ön koşul
            </Buton>
            <Buton
              gorunum="sessiz"
              onClick={() => setAcik((a) => (a === "arsiv" ? "kapali" : "arsiv"))}
            >
              Arşivle
            </Buton>
          </div>
        )}
      </div>

      {acik === "onkosul" && (
        <OnKosullar konu={konu} butunKonular={butunKonular} duzenleyebilir={duzenleyebilir} />
      )}
      {acik === "arsiv" && (
        <KonuArsivle konu={konu} alanId={alanId} onKapat={() => setAcik("kapali")} />
      )}
    </div>
  );
}

/**
 * ÖN KOŞUL — F-ACA-06
 *
 * ================================================================
 * MOTOR VARDI, KAPISI YOKTU
 * ================================================================
 * Döngü tespiti, uyarı üretimi ve program görevine uyarı iliştirme
 * 29.08.2026'da yazıldı ve canlı doğrulandı. Ama ön koşulu TANIMLAYAN
 * hiçbir ekran yoktu: sistem hiç ön koşul bilmediği için hiç uyarı
 * üretmiyordu. Bu projede tekrar eden sınıf — "mekanizma var ama bir
 * yol onu kullanmıyor".
 *
 * ================================================================
 * UYARIR, ENGELLEMEZ
 * ================================================================
 * Şartname "uyarı" diyor, "yasak" değil. Öğrenci konuyu okulda görmüş
 * olabilir ya da koç bilerek sırayı bozuyordur. Engelleseydi sistem,
 * koçun bilmediği bir şeyi bildiğini varsayardı.
 */
function OnKosullar({
  konu,
  butunKonular,
  duzenleyebilir,
}: {
  konu: AgacDugumu;
  butunKonular: AgacDugumu[];
  duzenleyebilir: boolean;
}) {
  const qc = useQueryClient();
  const [secim, setSecim] = useState("");

  const liste = useQuery({
    queryKey: ["onKosullar", konu.id],
    queryFn: () => onKosullariGetir(konu.id),
    retry: false,
  });

  const ekle = useMutation({
    mutationFn: (onKosulId: string) => onKosulEkle(konu.id, onKosulId),
    onSuccess: async () => {
      setSecim("");
      await qc.invalidateQueries({ queryKey: ["onKosullar", konu.id] });
    },
  });

  const kaldir = useMutation({
    mutationFn: (onKosulId: string) => onKosulKaldir(konu.id, onKosulId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["onKosullar", konu.id] });
    },
  });

  // Konunun kendisi ve HÂLİHAZIRDA ön koşul olanlar listeden düşüyor:
  // ikisi de sunucudan hata dönerdi ve seçilebilir görünmeleri
  // kullanıcıya reddedilecek bir iş yaptırmak olurdu.
  const mevcutlar = new Set((liste.data ?? []).map((o) => o.konuId));
  const secilebilir = butunKonular.filter((k) => k.id !== konu.id && !mevcutlar.has(k.id));

  return (
    <div className="rounded-kart border-notr-200 shadow-kart bg-notr-50 flex flex-col gap-2 border p-3">
      <p className="text-notr-600 text-xs">
        Bu konu işaretlenen konuları <strong>gerektiriyor</strong>. Tamamlanmamış bir ön koşul
        programa görev eklenirken <strong>uyarı</strong> üretir — engellemez.
      </p>

      {liste.isPending ? (
        <Iskelet satir={1} />
      ) : liste.isError ? (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {liste.error instanceof ApiHatasi ? liste.error.message : "Ön koşullar alınamadı."}
        </p>
      ) : liste.data.length === 0 ? (
        <p className="text-notr-600 text-sm">Bu konunun ön koşulu yok.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {liste.data.map((o) => (
            <li key={o.konuId} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-notr-700">
                {o.ad} <span className="text-notr-600">· {o.ders}</span>
              </span>
              {duzenleyebilir && (
                <Buton
                  gorunum="sessiz"
                  yukleniyor={kaldir.isPending && kaldir.variables === o.konuId}
                  onClick={() => kaldir.mutate(o.konuId)}
                >
                  Kaldır
                </Buton>
              )}
            </li>
          ))}
        </ul>
      )}

      {duzenleyebilir && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-64 grow">
            <Secim etiket="Ön koşul ekle" value={secim} onChange={(e) => setSecim(e.target.value)}>
              <option value="">Konu seçin</option>
              {secilebilir.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.ad}
                </option>
              ))}
            </Secim>
          </div>
          <Buton
            gorunum="ikincil"
            yukleniyor={ekle.isPending}
            disabled={secim === ""}
            onClick={() => ekle.mutate(secim)}
          >
            Ekle
          </Buton>
        </div>
      )}

      {/*
        DÖNGÜ REDDİ sunucudan geliyor ve mesajı hangi zincirin
        kapandığını söylüyor. Genel bir "eklenemedi" yazsaydık
        kullanıcı sebebini asla bulamazdı — ön koşul zincirleri gözle
        takip edilemeyecek kadar uzayabiliyor.
      */}
      {ekle.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {ekle.error instanceof ApiHatasi ? ekle.error.message : "Ön koşul eklenemedi."}
        </p>
      )}
      {kaldir.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {kaldir.error instanceof ApiHatasi ? kaldir.error.message : "Ön koşul kaldırılamadı."}
        </p>
      )}
    </div>
  );
}

/** Kalıcı silme YOK — arşivleme, gerekçesiyle (CLAUDE.md kural 7). */
function KonuArsivle({
  konu,
  alanId,
  onKapat,
}: {
  konu: AgacDugumu;
  alanId: string;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [gerekce, setGerekce] = useState("");

  const arsivle = useMutation({
    mutationFn: () => konuArsivle(konu.id, gerekce.trim()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["agac", alanId] });
      onKapat();
    },
  });

  return (
    <div className="rounded-kart shadow-kart border-uyari-200 bg-uyari-50 flex flex-col gap-2 border p-3">
      <p className="text-uyari-800 text-xs">
        <strong>{konu.ad}</strong> arşivlenecek. Silinmiyor: bu konuya bağlı geçmiş programlar ve
        deneme sonuçları yerinde kalıyor. Yeni programlara eklenemez hâle gelir.
      </p>
      <Alan
        etiket="Gerekçe"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        yardim="Denetim kaydına yazılıyor — altı ay sonra 'bu konu neden kayboldu' sorusunu cevaplayan tek şey bu."
      />
      {arsivle.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {arsivle.error instanceof ApiHatasi ? arsivle.error.message : "Arşivlenemedi."}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Buton
          yukleniyor={arsivle.isPending}
          disabled={gerekce.trim().length < 3}
          onClick={() => arsivle.mutate()}
        >
          Arşivle
        </Buton>
        <Buton gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}

/**
 * TOPLU GİRİŞ — önizle, sonra uygula.
 *
 * Önizleme ZORUNLU bir adım değil ama varsayılan yol: düğme sırası
 * "Önizle" → "Uygula". 200 satırlık bir yapıştırmanın ne yapacağını
 * görmeden kaydetmek, geri alması pahalı bir işlem.
 */
/**
 * TOPLU GİRİŞ.
 *
 * İÇERİDE KALIYOR — bir süre dışa verilmişti, geri alındı. Gerekçem
 * "ekran üzerinden ulaşmak derin gezinme ister" idi; ÖLÇÜNCE yanlış
 * çıktı: alanı seç, "Toplu ders ekle"ye bas — iki tık. Bileşeni
 * testin ihtiyacı için dışa vermek, ekranı teste uydurmak olurdu ve
 * aynı gün bunu başka iki yerde reddetmiştim.
 *
 * `cihazAdi`ı emsal göstermiştim; o SAF FONKSİYON ve CLAUDE.md saf
 * mantığın çıkarılmasını zaten öneriyor. Bir bileşeni render
 * edilebilsin diye açmak aynı şey değil.
 */
function TopluGiris({
  baslik,
  aciklama,
  ornek,
  calistir,
  tazele,
}: {
  baslik: string;
  aciklama: string;
  ornek: string;
  calistir: (metin: string, onIzleme: boolean) => Promise<TopluSonuc>;
  tazele: unknown[];
}) {
  const qc = useQueryClient();
  const [metin, setMetin] = useState("");
  const [sonuc, setSonuc] = useState<TopluSonuc | null>(null);

  const gonder = useMutation({
    mutationFn: (onIzleme: boolean) => calistir(metin, onIzleme),
    onSuccess: async (s) => {
      setSonuc(s);
      // Yalnizca GERCEKTEN yazildiysa listeyi tazele: onizlemede hicbir
      // sey degismedi ve gereksiz bir istek atmanin anlami yok.
      if (!s.onIzleme) {
        setMetin("");
        await qc.invalidateQueries({ queryKey: tazele });
      }
    },
  });

  const bos = metin.trim().length === 0;

  return (
    <Kart gorunum="unk" baslik={baslik} aciklama={aciklama}>
      <div className="flex flex-col gap-3">
        <MetinAlani
          etiket="Liste"
          rows={8}
          value={metin}
          onChange={(e) => setMetin(e.target.value)}
          placeholder={ornek}
          yardim="Excel'den ya da bir metin dosyasından olduğu gibi yapıştırabilirsiniz."
        />

        {gonder.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {gonder.error instanceof ApiHatasi ? gonder.error.message : "İşlem tamamlanamadı."}
          </p>
        )}

        {/*
          Sonuç paneli DUYURULUYOR (`role="status"`). Toplu içe aktarma
          bittiğinde ekranda beliren tek geri bildirim bu; ekran okuyucu
          kullanıcısı onu duymazsa işin bitip bitmediğini bilemez.
          Ayrım özellikle önemli: "eklenecek — henüz kaydedilmedi" ile
          "eklendi" bambaşka iki durum ve sessiz kalırsa kullanıcı
          önizlemeyi tamamlanmış sanabilir.
        */}
        {sonuc && (
          <div role="status" className="rounded-kart bg-notr-50 flex flex-col gap-2 p-3">
            <p className="text-notr-800 flex items-center gap-2 text-sm font-medium">
              <Ikon
                ad={sonuc.onIzleme ? "bekliyor" : "tamam"}
                className={sonuc.onIzleme ? "text-notr-600" : "text-onay-600"}
              />
              {sonuc.onIzleme
                ? `${sonuc.eklenen} satır eklenecek — henüz kaydedilmedi.`
                : `${sonuc.eklenen} satır eklendi.`}
            </p>

            {/*
              GERİ ALMA TAM BURADA — F-ACA-03b
              ================================================================
              Asıl ihtiyaç bu: "200 satır yapıştırdım, yanlış listeydi".
              Kullanıcıyı başka bir sekmeye gönderip partiyi tarihten
              bulmasını istemek, hatayı fark ettiği anla düzeltebildiği
              an arasına gereksiz bir mesafe koyardı.

              Önizlemede ÇIKMIYOR: ortada geri alınacak bir şey yok.
            */}
            {!sonuc.onIzleme && sonuc.partiId && (
              <div className="border-notr-200 border-t pt-2">
                <SonGirisiGeriAl partiId={sonuc.partiId} tazele={tazele} />
              </div>
            )}

            {/*
              ELENEN SATIRLAR SAYIYLA DEĞİL TEK TEK gösteriliyor.
              "12 satır elendi" demek, kullanıcıya hangilerini
              düzelteceğini söylemiyor — ve düzeltemediği satır yıl
              boyunca hiçbir programa girmiyor.
            */}
            {sonuc.elenen.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-uyari-800 text-sm font-medium">
                  {sonuc.elenen.length} satır alınmadı:
                </p>
                <ul className="flex flex-col gap-0.5">
                  {sonuc.elenen.map((e) => (
                    <li key={e.satirNo} className="text-notr-600 text-xs">
                      <span className="font-medium">{e.satirNo}. satır</span> — {e.mesaj}
                      {e.ham.trim().length > 0 && (
                        <span className="text-notr-600"> ({e.ham.trim()})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            gorunum="ikincil"
            disabled={bos}
            yukleniyor={gonder.isPending && gonder.variables === true}
            onClick={() => gonder.mutate(true)}
          >
            Önizle
          </Buton>
          <Buton
            disabled={bos}
            yukleniyor={gonder.isPending && gonder.variables === false}
            onClick={() => gonder.mutate(false)}
          >
            Uygula
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

// ---------------------------------------------------------------

/**
 * SÜRÜMLER — F-ACA-05, ve ondan önce F-PPL-11
 *
 * ================================================================
 * ZİNCİRİN SIFIRINCI HALKASI EKSİKTİ
 * ================================================================
 * Bu bölüm sürümü LİSTELEYİP YAYINLAYABİLİYOR ama AÇAMIYORDU. Boş
 * hâli "yayınlanmış sürüm olmadan öğrenciye müfredat kopyalanamaz"
 * diyordu ve sürüm açmanın yolu yoktu — yani doğru bilgiyi verip
 * çıkışsız bırakıyordu.
 *
 * Sebebi bir katman aşağıdaydı: sürüm `academicYearId` istiyor,
 * eğitim yılını üretecek hiçbir uç yoktu (02.09.2026'da ölçüldü).
 * Sistem boş açıldığı için de tohum bir yıl yazmıyor. Zincirin
 * tamamı: yıl → sürüm → konu ataması → yayın → öğrenciye kopya.
 */
function Surumler({ duzenleyebilir }: { duzenleyebilir: boolean }) {
  const qc = useQueryClient();
  const [yeniYil, setYeniYil] = useState(false);
  const [yeniSurum, setYeniSurum] = useState(false);
  const [konuAtanan, setKonuAtanan] = useState<string | null>(null);

  const yillar = useQuery({ queryKey: ["akademikYillar"], queryFn: yillariGetir, retry: false });
  const surumler = useQuery({ queryKey: ["surumler"], queryFn: surumleriGetir, retry: false });

  const yayinla = useMutation({
    mutationFn: (id: string) => surumYayinla(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["surumler"] });
    },
  });

  if (yillar.isPending || surumler.isPending) return <Iskelet satir={3} />;

  if (yillar.isError || surumler.isError) {
    const hata = yillar.error ?? surumler.error;
    return (
      <BosDurum
        ton="hata"
        baslik="Sürümler alınamadı"
        aciklama={hata instanceof ApiHatasi ? hata.message : undefined}
        eylem={
          <Buton
            gorunum="ikincil"
            onClick={() => {
              void yillar.refetch();
              void surumler.refetch();
            }}
          >
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  // --- Sifirinci adim: egitim yili ---
  if (yillar.data.length === 0) {
    if (yeniYil && duzenleyebilir) return <YeniYil onKapat={() => setYeniYil(false)} />;
    return (
      <BosDurum
        ikon="mufredat"
        baslik="Eğitim yılı tanımlanmamış"
        aciklama="Müfredat sürümü bir eğitim yılının altında açılır. Yıl olmadan sürüm, sürüm olmadan da öğrenciye müfredat atanamaz." /* F-PPL-11 */
        eylem={
          duzenleyebilir ? (
            <Buton onClick={() => setYeniYil(true)}>Eğitim yılı aç</Buton>
          ) : undefined
        }
      />
    );
  }

  if (yeniYil && duzenleyebilir) return <YeniYil onKapat={() => setYeniYil(false)} />;
  if (yeniSurum && duzenleyebilir) {
    return <YeniSurum yillar={yillar.data} onKapat={() => setYeniSurum(false)} />;
  }

  const isaretli = yillar.data.find((y) => y.gecerli) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <YilBandi yillar={yillar.data} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-notr-600 text-sm">
          {yillar.data.length} eğitim yılı · {surumler.data.length} sürüm
          {isaretli && ` · geçerli: ${isaretli.ad}`}
        </p>
        {duzenleyebilir && (
          <div className="flex flex-wrap gap-2">
            <Buton gorunum="ikincil" onClick={() => setYeniYil(true)}>
              Eğitim yılı aç
            </Buton>
            <Buton onClick={() => setYeniSurum(true)}>Yeni sürüm aç</Buton>
          </div>
        )}
      </div>

      {surumler.data.length === 0 ? (
        <BosDurum
          ikon="mufredat"
          baslik="Müfredat sürümü yok"
          aciklama="Sürüm, öğrenciye atanacak konu listesinin dondurulmuş hâlidir. Yayınlanmış bir sürüm olmadan öğrenciye müfredat kopyalanamaz." /* F-ACA-05 */
          eylem={
            duzenleyebilir ? (
              <Buton onClick={() => setYeniSurum(true)}>Yeni sürüm aç</Buton>
            ) : undefined
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {surumler.data.map((s) => {
            const d = SURUM_DURUMU[s.status] ?? { etiket: s.status, ton: "notr" as const };
            const taslak = s.status === "DRAFT";
            return (
              <li
                key={s.id}
                className="rounded-kart border-notr-200 shadow-kart flex flex-col gap-2 border p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-notr-900 text-sm font-medium">{s.name}</p>
                    <p className="text-notr-600 text-xs">{s.konuSayisi} konu</p>
                  </div>
                  <Rozet ton={d.ton}>{d.etiket}</Rozet>
                </div>

                {/*
                  YAYINLAMA GERİ ALINAMAZ (F-ACA-05): yayınlanmış sürüm
                  değiştirilemiyor, çünkü öğrencilere KOPYALANIYOR ve
                  sonradan değişse geçmiş programlar bozulurdu. Düğmeler
                  yalnızca taslakta çıkıyor.
                */}
                {duzenleyebilir && taslak && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap gap-2">
                      <Buton
                        gorunum="ikincil"
                        onClick={() => setKonuAtanan(konuAtanan === s.id ? null : s.id)}
                      >
                        {konuAtanan === s.id ? "Kapat" : "Konu ata"}
                      </Buton>
                      {/*
                        BOŞ SÜRÜM YAYINLANAMAZ (sunucu 400 döner):
                        konusuz bir müfredat öğrenciye atandığında boş
                        liste üretir ve hata sessizce ilerler. Düğmeyi
                        burada da kapatıyoruz ki kullanıcı reddedilen
                        bir işi denemesin — ama kural SUNUCUDA duruyor,
                        burası yalnızca onu tekrarlıyor.
                      */}
                      <Buton
                        disabled={s.konuSayisi === 0}
                        yukleniyor={yayinla.isPending && yayinla.variables === s.id}
                        onClick={() => yayinla.mutate(s.id)}
                      >
                        Yayınla
                      </Buton>
                    </div>
                    <p className="text-notr-600 text-xs">
                      {s.konuSayisi === 0
                        ? "Konusuz sürüm yayınlanamaz — önce konu atayın."
                        : "Yayınlandıktan sonra bu sürüm değiştirilemez — öğrencilere kopyalanır."}
                    </p>
                    {konuAtanan === s.id && <SurumKonulari surumId={s.id} />}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {yayinla.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {yayinla.error instanceof ApiHatasi ? yayinla.error.message : "Yayınlanamadı."}
        </p>
      )}
    </div>
  );
}

/**
 * İŞARET İLE TAKVİM AYRIŞTIYSA SÖYLE, SESSİZCE DÜZELTME.
 *
 * "Geçerli yıl" bayrağı elle işaretleniyor ve elle işaretlenen her
 * bayrak bir gün gerçekle ayrışır: yıl biter, kimse bayrağı taşımaz,
 * sistem aylarca geçmiş bir yılı geçerli sayar.
 *
 * Bayrağı tarihten TÜRETMİYORUZ — yeni yıl başlamadan "geçerli"
 * işaretlemek geçerli bir planlama ihtiyacı. Onun yerine ayrışmayı
 * görünür kılıyoruz; düzeltmek kullanıcının kararı.
 */
function YilBandi({ yillar }: { yillar: YilSatiri[] }) {
  const isaretli = yillar.find((y) => y.gecerli);
  const takvimdeki = yillar.find((y) => y.bugunIcinde);

  if (!isaretli) {
    return (
      <p className="rounded-alan border-uyari-200 bg-uyari-50 text-uyari-800 border p-3 text-sm">
        Hiçbir yıl <strong>geçerli</strong> işaretli değil.
        {takvimdeki && ` Takvime göre bugün "${takvimdeki.ad}" yılının içindeyiz.`}
      </p>
    );
  }

  if (!isaretli.bugunIcinde) {
    return (
      <p className="rounded-alan border-uyari-200 bg-uyari-50 text-uyari-800 border p-3 text-sm">
        Geçerli işaretli yıl <strong>{isaretli.ad}</strong>, ama bugün onun tarih aralığında değil.
        {takvimdeki
          ? ` Takvime göre "${takvimdeki.ad}" yılındayız.`
          : " Bugünü kapsayan tanımlı bir yıl da yok."}
      </p>
    );
  }

  return null;
}

function YeniYil({ onKapat }: { onKapat: () => void }) {
  const qc = useQueryClient();
  const [ad, setAd] = useState("");
  const [baslangic, setBaslangic] = useState("");
  const [bitis, setBitis] = useState("");
  const [gecerli, setGecerli] = useState(true);

  const ekle = useMutation({
    mutationFn: () => yilEkle({ ad: ad.trim(), baslangic, bitis, gecerli }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["akademikYillar"] });
      onKapat();
    },
  });

  const eksik = ad.trim().length === 0 || baslangic === "" || bitis === "";

  return (
    <Kart
      gorunum="unk"
      baslik="Yeni eğitim yılı"
      aciklama="Müfredat sürümleri ve dönem içi karşılaştırmalar bu yılın altında toplanır." /* F-PPL-11 */
    >
      <div className="flex flex-col gap-4">
        <Alan
          etiket="Ad"
          value={ad}
          onChange={(e) => setAd(e.target.value)}
          yardim="Örnek: 2026-2027"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Başlangıç"
            type="date"
            value={baslangic}
            onChange={(e) => setBaslangic(e.target.value)}
          />
          <Alan
            etiket="Bitiş"
            type="date"
            value={bitis}
            onChange={(e) => setBitis(e.target.value)}
          />
        </div>

        {/*
          Yıllar ÇAKIŞAMAZ; kural sunucuda ve gerekçesi orada yazılı
          (`egitim-yili.ts`). Burada yalnızca kullanıcıya önceden
          söylüyoruz — reddi sunucudan duymak, formu doldurduktan
          sonra öğrenmek demek.
        */}
        <p className="text-notr-600 text-xs">
          Tarihler var olan bir yılla tek gün bile kesişemez: bir gün iki yıla birden ait olursa
          dönem içi karşılaştırma sessizce başka bir küme seçer.
        </p>

        <label className="text-notr-800 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={gecerli}
            onChange={(e) => setGecerli(e.target.checked)}
            className="rounded-alan accent-marka-600 size-4"
          />
          Bu yılı <strong>geçerli</strong> yıl olarak işaretle
        </label>
        <p className="text-notr-600 -mt-2 text-xs">
          Geçerli yıl tektir — işaretlerseniz varsa öteki yılın işareti aynı işlemde düşer.
        </p>

        {ekle.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {ekle.error instanceof ApiHatasi ? ekle.error.message : "Eğitim yılı açılamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton yukleniyor={ekle.isPending} disabled={eksik} onClick={() => ekle.mutate()}>
            Yılı aç
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

function YeniSurum({ yillar, onKapat }: { yillar: YilSatiri[]; onKapat: () => void }) {
  const qc = useQueryClient();
  const alanlar = useQuery({ queryKey: ["alanlar"], queryFn: alanlariGetir, retry: false });
  // Varsayilan, isaretli yil: yeni surum neredeyse her zaman gecerli
  // yila acilir ve her seferinde secmek gereksiz bir adim.
  const [yilId, setYilId] = useState(() => (yillar.find((y) => y.gecerli) ?? yillar[0])?.id ?? "");
  const [ad, setAd] = useState("");
  const [alanId, setAlanId] = useState("");

  const ekle = useMutation({
    mutationFn: () =>
      surumEkle({ academicYearId: yilId, name: ad.trim(), fieldId: alanId === "" ? null : alanId }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["surumler"] });
      onKapat();
    },
  });

  return (
    <Kart
      gorunum="unk"
      baslik="Yeni müfredat sürümü"
      aciklama="Sürüm TASLAK açılır. Konular atanıp yayınlanana kadar öğrenciye kopyalanamaz." /* F-ACA-05 */
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Secim etiket="Eğitim yılı" value={yilId} onChange={(e) => setYilId(e.target.value)}>
            {yillar.map((y) => (
              <option key={y.id} value={y.id}>
                {y.ad}
                {y.gecerli ? " (geçerli)" : ""}
              </option>
            ))}
          </Secim>
          <Alan
            etiket="Sürüm adı"
            value={ad}
            onChange={(e) => setAd(e.target.value)}
            yardim="Aynı yılda aynı ad iki kez kullanılamaz."
          />
        </div>

        <Secim
          etiket="Alan"
          value={alanId}
          onChange={(e) => setAlanId(e.target.value)}
          yardim="Boş bırakılırsa sürüm alandan bağımsız olur."
        >
          <option value="">Alan seçilmedi</option>
          {(alanlar.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Secim>

        {ekle.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {ekle.error instanceof ApiHatasi ? ekle.error.message : "Sürüm açılamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={ekle.isPending}
            disabled={yilId === "" || ad.trim().length === 0}
            onClick={() => ekle.mutate()}
          >
            Sürümü aç
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

/**
 * SÜRÜME KONU ATAMA.
 *
 * ================================================================
 * YALNIZCA KONU SEÇİLEBİLİR — ÜNİTE SEÇİLEMEZ
 * ================================================================
 * Ağaçta ünite ile konu dersin altında aynı düzeyde duruyor. Sunucu
 * ünite id'sini reddeder (400) ama kullanıcı hangi satırın sorunlu
 * olduğunu göremezdi. Ağaç artık `tur` taşıyor; ünite başlık olarak
 * çiziliyor, seçilemiyor.
 */
export function SurumKonulari({ surumId }: { surumId: string }) {
  const qc = useQueryClient();
  const [alanId, setAlanId] = useState("");
  const [secili, setSecili] = useState<Set<string>>(new Set());

  const alanlar = useQuery({ queryKey: ["alanlar"], queryFn: alanlariGetir, retry: false });
  const agac = useQuery({
    queryKey: ["agac", alanId],
    queryFn: () => agacGetir(alanId),
    enabled: alanId !== "",
    retry: false,
  });

  /** Gönderilen sayı — `atamaOzeti` farkı buradan hesaplıyor. */
  const [istenen, setIstenen] = useState(0);

  const ata = useMutation({
    mutationFn: () => {
      setIstenen(secili.size);
      return surumeKonuAta(surumId, [...secili]);
    },
    onSuccess: async () => {
      setSecili(new Set());
      await qc.invalidateQueries({ queryKey: ["surumler"] });
    },
  });

  function degistir(id: string) {
    setSecili((onceki) => {
      const yeni = new Set(onceki);
      if (yeni.has(id)) yeni.delete(id);
      else yeni.add(id);
      return yeni;
    });
  }

  // "Tümünü seç" (UI/UX Dalga 2, M7): mantık `konu-secimi.ts`te saf ve testli.
  const tumKimlikler = useMemo(() => konuKimlikleri(agac.data ?? []), [agac.data]);
  const tumDurum = secimDurumu(tumKimlikler, secili);

  return (
    <div className="rounded-kart border-notr-200 shadow-kart bg-notr-50 flex flex-col gap-3 border p-4">
      <Secim
        etiket="Alan"
        value={alanId}
        onChange={(e) => {
          setAlanId(e.target.value);
          setSecili(new Set());
        }}
      >
        <option value="">Alan seçin</option>
        {(alanlar.data ?? []).map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </Secim>

      {alanId === "" ? (
        <p className="text-notr-600 text-sm">Konuları görmek için bir alan seçin.</p>
      ) : agac.isPending ? (
        <Iskelet satir={3} />
      ) : agac.isError ? (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {agac.error instanceof ApiHatasi ? agac.error.message : "Ders ağacı alınamadı."}
        </p>
      ) : agac.data.length === 0 ? (
        <p className="text-notr-600 text-sm">Bu alanda ders yok.</p>
      ) : (
        <>
          {/*
            TOPLU SEÇİM — alanda yüzlerce konu olabiliyor ve atama tek tek
            kutu işaretlemekti. Sayı canlı bölgede: ekran okuyucu "N / M konu
            seçili" duyuyor.
          */}
          <div className="flex flex-wrap items-center gap-2">
            <Buton
              gorunum="ikincil"
              disabled={tumKimlikler.length === 0}
              onClick={() => setSecili((o) => kumeyiDegistir(o, tumKimlikler))}
            >
              {tumDurum === "hepsi" ? "Seçimi bırak" : `Tümünü seç (${tumKimlikler.length})`}
            </Buton>
            <span role="status" aria-live="polite" className="text-notr-600 text-sm">
              {secili.size} / {tumKimlikler.length} konu seçili
            </span>
          </div>
          <ul className="flex max-h-96 flex-col gap-3 overflow-y-auto">
            {agac.data.map((ders) => {
              const dersKimlikleri = konuKimlikleri(ders.cocuklar ?? []);
              return (
                <li key={ders.id}>
                  <DersSecimi
                    ad={ders.ad}
                    durum={secimDurumu(dersKimlikleri, secili)}
                    konuSayisi={dersKimlikleri.length}
                    onDegistir={() => setSecili((o) => kumeyiDegistir(o, dersKimlikleri))}
                  />
                  <KonuSecimi dugumler={ders.cocuklar ?? []} secili={secili} degistir={degistir} />
                </li>
              );
            })}
          </ul>
        </>
      )}

      {ata.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {ata.error instanceof ApiHatasi ? ata.error.message : "Konular atanamadı."}
        </p>
      )}
      {ata.isSuccess && (
        <p className="text-onay-700 text-sm font-medium">{atamaOzeti(istenen, ata.data.eklenen)}</p>
      )}

      <div>
        <Buton yukleniyor={ata.isPending} disabled={secili.size === 0} onClick={() => ata.mutate()}>
          {secili.size === 0 ? "Konu seçin" : `${secili.size} konuyu sürüme ekle`}
        </Buton>
      </div>
    </div>
  );
}

/** Ders başlığı + dersin bütün konuları için üç durumlu onay kutusu. */
function DersSecimi({
  ad,
  durum,
  konuSayisi,
  onDegistir,
}: {
  ad: string;
  durum: SecimDurumu;
  konuSayisi: number;
  onDegistir: () => void;
}) {
  const kutu = useRef<HTMLInputElement>(null);
  // `indeterminate` HTML özniteliği değil, yalnız DOM özelliği: JSX ile verilemez.
  useEffect(() => {
    if (kutu.current) kutu.current.indeterminate = durum === "kismi";
  }, [durum]);
  return (
    <label className="min-h-dokunma text-notr-900 flex items-center gap-2 text-sm font-medium">
      <input
        ref={kutu}
        type="checkbox"
        checked={durum === "hepsi"}
        disabled={konuSayisi === 0}
        onChange={onDegistir}
        aria-label={`${ad} — dersin bütün konularını seç`}
        className="rounded-alan accent-marka-600 size-4"
      />
      {ad}
    </label>
  );
}

function KonuSecimi({
  dugumler,
  secili,
  degistir,
  derinlik = 0,
}: {
  dugumler: AgacDugumu[];
  secili: Set<string>;
  degistir: (id: string) => void;
  derinlik?: number;
}) {
  return (
    <ul className="flex flex-col" style={{ marginInlineStart: derinlik * 12 }}>
      {dugumler.map((d) =>
        d.tur === "unite" ? (
          <li key={d.id}>
            <p className="text-notr-600 mt-1 text-xs font-medium tracking-wide uppercase">{d.ad}</p>
            <KonuSecimi
              dugumler={d.cocuklar ?? []}
              secili={secili}
              degistir={degistir}
              derinlik={derinlik + 1}
            />
          </li>
        ) : (
          <li key={d.id}>
            <label className="min-h-dokunma text-notr-700 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={secili.has(d.id)}
                onChange={() => degistir(d.id)}
                className="rounded-alan accent-marka-600 size-4"
              />
              {d.ad}
            </label>
            {d.cocuklar && d.cocuklar.length > 0 && (
              <KonuSecimi
                dugumler={d.cocuklar}
                secili={secili}
                degistir={degistir}
                derinlik={derinlik + 1}
              />
            )}
          </li>
        ),
      )}
    </ul>
  );
}
